import React, { useState } from 'react';
import { Calendar, Clock, Users, BookOpen, Building, ChevronLeft, ChevronRight } from 'lucide-react';

const ScheduleDisplay = ({ 
  schedule, 
  formData,
  activeView
}) => {
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [currentGroupId, setCurrentGroupId] = useState(null);
  const [currentDateStr, setCurrentDateStr] = useState(null);
  
  if (!schedule || !schedule.schedule || schedule.schedule.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center">
        <p className="text-gray-500 dark:text-gray-400">Расписание еще не сгенерировано</p>
      </div>
    );
  }
  
  // Получаем все даты, для которых есть расписание
  const allDates = Object.keys(schedule.byDay).sort();
  
  // Получаем все недели, для которых есть расписание
  const weeks = Object.keys(schedule.byWeek).sort();
  
  // Устанавливаем текущую группу при первом рендеринге
  if (!currentGroupId && formData.groups.length > 0) {
    setCurrentGroupId(formData.groups[0].id);
  }
  
  // Устанавливаем текущую дату при первом рендеринге
  if (!currentDateStr && allDates.length > 0) {
    setCurrentDateStr(allDates[0]);
  }
  
  // Функция для перехода к следующей неделе
  const nextWeek = () => {
    if (currentWeekIndex < weeks.length - 1) {
      setCurrentWeekIndex(currentWeekIndex + 1);
    }
  };
  
  // Функция для перехода к предыдущей неделе
  const prevWeek = () => {
    if (currentWeekIndex > 0) {
      setCurrentWeekIndex(currentWeekIndex - 1);
    }
  };
  
  // Функция для форматирования даты в читаемом виде
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('ru-RU', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      weekday: 'long'
    }).format(date);
  };
  
  // Функция для форматирования краткой даты
  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('ru-RU', { 
      day: 'numeric', 
      month: 'short'
    }).format(date);
  };
  
  // Функция для получения названия дня недели
  const getDayOfWeek = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('ru-RU', { weekday: 'long' }).format(date);
  };
  
  // Функция для получения сокращенного названия дня недели
  const getShortDayOfWeek = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('ru-RU', { weekday: 'short' }).format(date);
  };
  
  // Рендер содержимого в зависимости от выбранного вида
  const renderContent = () => {
    switch (activeView) {
      case 'day':
        return renderDayView();
      case 'week':
        return renderWeekView();
      case 'month':
        return renderMonthView();
      case 'list':
        return renderListView();
      default:
        return renderWeekView();
    }
  };
  
  // Рендер представления на день
  const renderDayView = () => {
    if (!currentDateStr || !schedule.byDay[currentDateStr]) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-gray-500 dark:text-gray-400">Нет занятий на выбранную дату</p>
        </div>
      );
    }
    
    const events = schedule.byDay[currentDateStr];
    
    // Сортируем события по времени начала
    const sortedEvents = [...events].sort((a, b) => {
      return a.startMinutes - b.startMinutes;
    });
    
    return (
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {formatDate(currentDateStr)}
        </h3>
        
        <div className="space-y-2">
          {sortedEvents.map(event => (
            <div 
              key={event.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                  <span className="font-medium">{event.startTime} - {event.endTime}</span>
                </div>
                <div>
                  <span 
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: `${event.subjectColor}20`,
                      color: event.subjectColor
                    }}
                  >
                    {event.groupName}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center mb-1">
                <BookOpen className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                <span 
                  className="font-medium"
                  style={{ color: event.subjectColor }}
                >
                  {event.subjectName}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  <span>{event.teacherName}</span>
                </div>
                <div className="flex items-center">
                  <Building className="w-4 h-4 mr-1" />
                  <span>Кабинет {event.roomName}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Рендер представления на неделю
  const renderWeekView = () => {
    if (weeks.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-gray-500 dark:text-gray-400">Нет данных для отображения недельного расписания</p>
        </div>
      );
    }
    
    const currentWeek = weeks[currentWeekIndex];
    const weekDates = Object.keys(schedule.byWeek[currentWeek]).sort();
    
    // Получаем дату начала и конца недели для отображения
    const weekStart = new Date(weekDates[0]);
    const weekEnd = new Date(weekDates[weekDates.length - 1]);
    
    const weekStartFormatted = new Intl.DateTimeFormat('ru-RU', { 
      day: 'numeric', 
      month: 'long'
    }).format(weekStart);
    
    const weekEndFormatted = new Intl.DateTimeFormat('ru-RU', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric'
    }).format(weekEnd);
    
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <button 
            onClick={prevWeek}
            disabled={currentWeekIndex === 0}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {weekStartFormatted} - {weekEndFormatted}
          </h3>
          
          <button 
            onClick={nextWeek}
            disabled={currentWeekIndex === weeks.length - 1}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {weekDates.map(date => {
            const events = schedule.byWeek[currentWeek][date] || [];
            const sortedEvents = [...events].sort((a, b) => a.startMinutes - b.startMinutes);
            
            return (
              <div 
                key={date}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                <div className="bg-indigo-50 dark:bg-indigo-900/30 py-2 px-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="font-medium text-indigo-700 dark:text-indigo-300">
                    {getShortDayOfWeek(date)}
                  </div>
                  <div className="text-xs text-indigo-600 dark:text-indigo-400">
                    {formatShortDate(date)}
                  </div>
                </div>
                
                <div className="p-2 schedule-display max-h-[500px] overflow-y-auto">
                  {sortedEvents.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                      Нет занятий
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {sortedEvents.map(event => (
                        <div
                          key={event.id}
                          className="p-2 rounded-md text-xs"
                          style={{ 
                            backgroundColor: `${event.subjectColor}15`,
                            borderLeft: `3px solid ${event.subjectColor}`
                          }}
                        >
                          <div className="font-medium mb-1">{event.startTime} - {event.endTime}</div>
                          <div 
                            className="font-medium mb-1" 
                            style={{ color: event.subjectColor }}
                          >
                            {event.subjectName}
                          </div>
                          <div className="text-gray-600 dark:text-gray-400">Группа: {event.groupName}</div>
                          <div className="text-gray-600 dark:text-gray-400">Кабинет: {event.roomName}</div>
                          <div className="text-gray-600 dark:text-gray-400 truncate" title={event.teacherName}>
                            {event.teacherName}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  // Рендер представления на месяц
  const renderMonthView = () => {
    // Группируем даты по месяцам
    const months = allDates.reduce((acc, dateStr) => {
      const date = new Date(dateStr);
      const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
      
      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }
      
      acc[monthKey].push(dateStr);
      return acc;
    }, {});
    
    const monthKeys = Object.keys(months).sort();
    
    if (monthKeys.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-gray-500 dark:text-gray-400">Нет данных для отображения месячного расписания</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        {monthKeys.map(monthKey => {
          const [year, month] = monthKey.split('-').map(Number);
          const monthName = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(new Date(year, month - 1, 1));
          
          return (
            <div key={monthKey}>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                {monthName}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {months[monthKey].map(dateStr => {
                  const events = schedule.byDay[dateStr] || [];
                  const totalEvents = events.length;
                  
                  return (
                    <div 
                      key={dateStr}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setCurrentDateStr(dateStr)}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="font-medium text-gray-800 dark:text-gray-200">
                          {getDayOfWeek(dateStr)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatShortDate(dateStr)}
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Всего занятий: <span className="font-medium text-indigo-600 dark:text-indigo-400">{totalEvents}</span>
                      </div>
                      
                      {totalEvents > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {formData.subjects
                            .filter(subject => events.some(event => event.subject === subject.id))
                            .map(subject => (
                              <span 
                                key={subject.id}
                                className="inline-block w-3 h-3 rounded-full"
                                style={{ backgroundColor: subject.color }}
                                title={subject.name}
                              ></span>
                            ))
                          }
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  
  // Рендер списочного представления
  const renderListView = () => {
    if (!currentGroupId) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-gray-500 dark:text-gray-400">Выберите группу/класс для просмотра расписания</p>
        </div>
      );
    }
    
    // Получаем все даты для текущей группы
    const groupDates = Object.keys(schedule.byGroup[currentGroupId] || {}).sort();
    
    if (groupDates.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-gray-500 dark:text-gray-400">Нет занятий для выбранной группы/класса</p>
        </div>
      );
    }
    
    return (
      <div>
        <div className="mb-4">
          <label htmlFor="groupSelect" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Выберите группу/класс
          </label>
          <select
            id="groupSelect"
            value={currentGroupId}
            onChange={(e) => setCurrentGroupId(e.target.value)}
            className="form-select"
          >
            {formData.groups.map(group => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="space-y-6">
          {groupDates.map(dateStr => {
            const events = schedule.byGroup[currentGroupId][dateStr] || [];
            const sortedEvents = [...events].sort((a, b) => a.startMinutes - b.startMinutes);
            
            return (
              <div key={dateStr}>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  {formatDate(dateStr)}
                </h3>
                
                <div className="space-y-2">
                  {sortedEvents.map(event => (
                    <div 
                      key={event.id}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                          <span className="font-medium">{event.startTime} - {event.endTime}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center mb-1">
                        <BookOpen className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                        <span 
                          className="font-medium"
                          style={{ color: event.subjectColor }}
                        >
                          {event.subjectName}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          <span>{event.teacherName}</span>
                        </div>
                        <div className="flex items-center">
                          <Building className="w-4 h-4 mr-1" />
                          <span>Кабинет {event.roomName}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6 schedule-display">
      {/* Метаданные расписания */}
      <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <div className="text-xs text-indigo-600 dark:text-indigo-400">Период</div>
            <div className="font-medium text-indigo-800 dark:text-indigo-200">
              {new Date(schedule.startDate).toLocaleDateString('ru-RU')} - {new Date(schedule.endDate).toLocaleDateString('ru-RU')}
            </div>
          </div>
          <div>
            <div className="text-xs text-indigo-600 dark:text-indigo-400">Учебных дней</div>
            <div className="font-medium text-indigo-800 dark:text-indigo-200">{schedule.meta.totalDays}</div>
          </div>
          <div>
            <div className="text-xs text-indigo-600 dark:text-indigo-400">Всего занятий</div>
            <div className="font-medium text-indigo-800 dark:text-indigo-200">{schedule.meta.totalAssignments}</div>
          </div>
          <div>
            <div className="text-xs text-indigo-600 dark:text-indigo-400">Дата генерации</div>
            <div className="font-medium text-indigo-800 dark:text-indigo-200">
              {new Date(schedule.meta.generatedAt).toLocaleString('ru-RU')}
            </div>
          </div>
        </div>
      </div>
      
      {/* Содержимое в зависимости от выбранного вида */}
      {renderContent()}
    </div>
  );
};

export default ScheduleDisplay;